import { Button, Paper, Text } from '@mantine/core'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { followupVisitTopicsSpecialSchema, visitTopicAnchor } from '@behuman/core-contract'
import type { FollowupVisitTopicWire } from '@behuman/core-contract'
import { cn } from '@/utils/cn'
import type { SpecialViewProps } from './specials-registry'

// Visual skin. 'light' (default) is the standalone white Paper card used on the
// Assistant surfaces; 'dark' is a frameless card stack for dark surfaces — the
// Zoom call rail provides its own section frame and header.
type TopicsVariant = 'light' | 'dark'

type FollowupVisitTopicsProps = SpecialViewProps & {
  // Show a per-topic "Hide" action (off by default — every surface is
  // display-only today; the live-visit copilot will drive it later).
  showHideButton?: boolean
  onHide?: (topicId: number) => void
  variant?: TopicsVariant
}

// Category chips take their color from the topic's priority.
const priorityChipClasses: Record<
  TopicsVariant,
  Record<FollowupVisitTopicWire['priority'], string>
> = {
  light: {
    critical: 'border-purple-200 bg-purple-50 text-purple-700',
    high: 'border-red-200 bg-red-50 text-red-700',
    medium: 'border-amber-200 bg-amber-50 text-amber-700',
    low: 'border-blue-200 bg-blue-50 text-blue-700',
  },
  dark: {
    critical: 'border-purple-500/30 bg-purple-500/15 text-purple-300',
    high: 'border-red-500/30 bg-red-500/15 text-red-300',
    medium: 'border-amber-500/30 bg-amber-500/15 text-amber-300',
    low: 'border-blue-500/30 bg-blue-500/15 text-blue-300',
  },
}

const variantStyles = {
  light: {
    card: 'rounded-lg border border-[#e2e8f0] bg-white px-3 py-2.5',
    titleColor: undefined,
    chip: 'rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase',
    question: 'mt-1 rounded-md bg-slate-50 px-2.5 py-1.5 text-xs text-slate-800 italic',
    basedOn: 'mt-1 text-xs text-slate-600',
    evidence: 'mt-1.5',
    pill: 'rounded-full border px-2 py-0.5 text-[11px] transition',
    pillActive: 'border-[#bfdbfe] bg-[#eff6ff] text-[#2563eb]',
    pillIdle: 'border-[#e2e8f0] bg-white text-slate-600 hover:border-[#bfdbfe] hover:bg-[#eff6ff]',
    detail:
      'mt-1.5 rounded-md border border-[#e2e8f0] bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700',
    link: 'font-medium text-[#2563eb] underline underline-offset-2',
    list: 'flex flex-col gap-2 p-3',
    empty: 'p-3 text-xs text-slate-500 italic',
    hideButtonColor: 'gray',
  },
  // Dark sizes sit on the scribe panel's 5-size scale (scribeText.ts —
  // 10.5/12/14px) so the cards stay harmonic with the rail around them.
  dark: {
    card: 'rounded-lg border border-[#1e293b] bg-[#0b1220] px-3 py-2.5',
    titleColor: 'gray.1',
    chip: 'rounded-full border px-2 py-0.5 text-[10.5px] font-semibold tracking-wide uppercase',
    question: 'mt-1 text-[12px] leading-relaxed text-slate-300',
    basedOn: 'mt-1.5 text-[12px] leading-relaxed text-slate-400',
    evidence: 'mt-1.5',
    // `!` on the font size: the app's layer statement registers `mantine`
    // above `utilities` (index.css:7 runs after the tailwindcss import), so
    // Mantine's `font: inherit` button reset beats font utilities on raw
    // <button>s unless the declaration is important.
    pill: 'rounded-full border px-2.5 py-[3px] text-[10.5px]! transition',
    pillActive: 'border-blue-400/40 bg-blue-500/15 text-blue-300',
    pillIdle:
      'border-white/[0.08] bg-white/[0.06] text-slate-300 hover:border-blue-400/40 hover:bg-blue-500/10',
    detail:
      'mt-1.5 rounded-md border border-[#1e293b] bg-white/[0.04] px-2.5 py-1.5 text-[12px] text-slate-300',
    link: 'font-medium text-blue-300 underline underline-offset-2',
    list: 'flex flex-col gap-2',
    empty: 'text-[12px] text-slate-400 italic',
    hideButtonColor: 'gray.4',
  },
} as const

// Display labels for the fixed category vocabulary (slugs defined in the
// middleware contract, FOLLOWUP_VISIT_TOPIC_CATEGORIES).
const categoryLabels: Record<FollowupVisitTopicWire['categories'][number], string> = {
  'data-integrity': 'Data Integrity',
  'safety-alert': 'Safety Alert',
  risk: 'Risk',
  'care-gap': 'Care Gap',
  monitoring: 'Monitoring',
  documentation: 'Documentation',
}

// Alex's requested copy minus the "documentation appears complete" claim — an
// empty topic list can also result from an incomplete chart snapshot, so the
// UI must not assert completeness (Greptile P1 on MR !1259).
const EMPTY_STATE_TEXT =
  'No significant clinical concerns identified based on the available information.'

// Pill caption: "label — value · date" ("A1c — 9.2% · 03/12/2026").
const evidenceCaption = (e: { label: string; value: string; date?: string }): string =>
  `${e.label} — ${e.value}${e.date ? ` · ${e.date}` : ''}`

// Source links come from the chart input (in-app hrefs go through react-router;
// anything else opens in a new tab). Guidelines are never linked by contract.
const EvidenceSourceLink = ({ href, className }: { href: string; className: string }) =>
  href.startsWith('/') ? (
    <Link to={href} className={className}>
      View source
    </Link>
  ) : (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      View source
    </a>
  )

const TopicCard = ({
  topic,
  rank,
  variant,
  showHideButton,
  onHide,
}: {
  topic: FollowupVisitTopicWire
  /** 1-based position in the rendered list — the dark card's leading number. */
  rank: number
  variant: TopicsVariant
  showHideButton: boolean
  onHide?: (topicId: number) => void
}) => {
  // Index of the evidence entry whose detail is expanded, if any.
  const [openEvidence, setOpenEvidence] = useState<number | null>(null)
  const styles = variantStyles[variant]
  const chipClass = priorityChipClasses[variant][topic.priority]
  const expanded = openEvidence !== null ? topic.evidence[openEvidence] : undefined

  const chips = topic.categories.map((category) => (
    <span key={category} className={cn(styles.chip, chipClass)}>
      {categoryLabels[category]}
    </span>
  ))
  const hideButton = showHideButton && (
    <Button
      size="compact-xs"
      variant="subtle"
      color={styles.hideButtonColor}
      className="shrink-0"
      onClick={() => onHide?.(topic.id)}
    >
      Hide
    </Button>
  )

  return (
    // The light card is the report's (Risks & Gaps): a `#visit-topic-…` link lands on it.
    <div
      id={variant === 'light' ? visitTopicAnchor(topic.title) : undefined}
      className={cn(styles.card, 'scroll-mt-24')}
    >
      {variant === 'dark' ? (
        <>
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
              {chips}
              <Text size="sm" fw={600} c={styles.titleColor} span>
                {rank}. {topic.title}
              </Text>
            </div>
            {hideButton}
          </div>
          <div className={styles.basedOn}>
            <span className="font-semibold text-slate-300">Why ask:</span> {topic.basedOn}
          </div>
          <div className={styles.question}>
            <span className="font-semibold text-slate-200">Suggested question:</span>{' '}
            {topic.question}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-start justify-between gap-2">
            <div>
              {topic.categories.length > 0 && (
                <div className="mb-1 flex flex-wrap gap-1">{chips}</div>
              )}
              <Text size="sm" fw={600} c={styles.titleColor}>
                {topic.title}
              </Text>
            </div>
            {hideButton}
          </div>
          <div className={styles.question}>“{topic.question}”</div>
          <div className={styles.basedOn}>
            <span className="font-semibold">Based on:</span> {topic.basedOn}
          </div>
        </>
      )}
      {topic.evidence.length > 0 && (
        <div className={styles.evidence}>
          <div className="flex flex-wrap gap-1">
            {topic.evidence.map((entry, index) => (
              <button
                key={`${entry.label}-${index}`}
                type="button"
                onClick={() => setOpenEvidence((current) => (current === index ? null : index))}
                className={cn(
                  styles.pill,
                  openEvidence === index ? styles.pillActive : styles.pillIdle,
                )}
              >
                {evidenceCaption(entry)}
              </button>
            ))}
          </div>
          {expanded && (expanded.detail || expanded.href) && (
            <div className={styles.detail}>
              {expanded.detail && <p className="mb-1 last:mb-0">{expanded.detail}</p>}
              {expanded.href && <EvidenceSourceLink href={expanded.href} className={styles.link} />}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Renders the `followup-visit-topics` special (PD-1379): the prioritized topics
// the practitioner may want to address at the patient's next visit, most
// critical first (the server-stamped topic order). A special with zero topics
// is the agent's explicit "nothing qualifies" — rendered as an empty-state
// card. The special arrives loose off the wire, so it's narrowed against the
// strict schema first — a payload that doesn't validate (including reports
// stored with the pre-PD-1379-revision topic shape) renders nothing rather
// than crashing the surface.
export const FollowupVisitTopics = ({
  special,
  showHideButton = false,
  onHide,
  variant = 'light',
}: FollowupVisitTopicsProps) => {
  const parsed = followupVisitTopicsSpecialSchema.safeParse(special)
  if (!parsed.success) return null

  const styles = variantStyles[variant]
  const body =
    parsed.data.topics.length === 0 ? (
      <div className={styles.empty}>{EMPTY_STATE_TEXT}</div>
    ) : (
      <div className={styles.list}>
        {parsed.data.topics.map((topic, index) => (
          <TopicCard
            key={topic.id}
            topic={topic}
            rank={index + 1}
            variant={variant}
            showHideButton={showHideButton}
            onHide={onHide}
          />
        ))}
      </div>
    )

  if (variant === 'dark') return body

  return (
    <Paper withBorder radius="md" className="overflow-hidden">
      <div className="border-b px-3.5 py-2.5">
        <Text size="sm" fw={700}>
          Visit Guidance
        </Text>
      </div>
      {body}
    </Paper>
  )
}
